import { useState } from "react";
import { Bell, Check, ChevronDown, HelpCircle, LogOut, Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useCompany } from "@/contexts/CompanyContext";
import { cn } from "@/lib/utils";

function getInitials(email: string): string {
  const name = email.split("@")[0];
  const parts = name.split(/[._-]/);
  if (parts.length >= 2) {
    return (parts[0][0] + parts[1][0]).toUpperCase();
  }
  return name.slice(0, 2).toUpperCase();
}

async function signOut() {
  const csrfToken = document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content ?? "";
  await fetch("/users/sign_out", {
    method: "DELETE",
    headers: { "X-CSRF-Token": csrfToken },
  });
  window.location.href = "/users/sign_in";
}

export function TopBar() {
  const { company, companies, switchCompany, currentUser } = useCompany();
  const [open, setOpen] = useState(false);

  const userEmail = currentUser?.email ?? "";
  const userRole = currentUser?.role ?? "";
  const initials = getInitials(userEmail);

  return (
    <header className="sticky top-0 z-30 flex h-14 items-center gap-3 border-b border-border bg-background/95 px-6 backdrop-blur">
      <div className="relative">
        <button
          onClick={() => setOpen(!open)}
          className="flex items-center gap-2 rounded-md border border-border bg-card px-2.5 py-1.5 hover:bg-muted/40 transition-colors"
        >
          <div className="h-5 w-5 rounded bg-gradient-to-br from-primary to-info" />
          <span className="text-sm font-medium">{company?.name ?? "Select company"}</span>
          <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
        </button>

        {open && companies.length > 1 && (
          <div className="absolute left-0 top-full mt-1 w-64 rounded-lg border border-border bg-card p-1 shadow-lg">
            {companies.map((c) => (
              <button
                key={c.id}
                onClick={() => { switchCompany(c.id); setOpen(false); }}
                className={cn(
                  "flex w-full items-center gap-2 rounded-md px-3 py-2 text-left text-sm transition-colors hover:bg-muted/40",
                  c.id === company?.id && "bg-muted/40"
                )}
              >
                <div className="h-5 w-5 shrink-0 rounded bg-gradient-to-br from-primary to-info" />
                <div className="flex-1 truncate">
                  <div className="font-medium">{c.name}</div>
                  <div className="text-[10px] text-muted-foreground capitalize">{c.plan} &middot; {c.status}</div>
                </div>
                {c.id === company?.id && <Check className="h-4 w-4 text-primary" />}
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="relative ml-2 hidden flex-1 max-w-md md:block">
        <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input placeholder="Search messages, templates, contacts..." className="h-9 pl-8" />
      </div>

      <div className="ml-auto flex items-center gap-1">
        <Button variant="ghost" size="icon" aria-label="Help">
          <HelpCircle className="h-4 w-4" />
        </Button>
        <Button variant="ghost" size="icon" aria-label="Notifications" className="relative">
          <Bell className="h-4 w-4" />
          <span className="absolute right-1.5 top-1.5 h-1.5 w-1.5 rounded-full bg-destructive" />
        </Button>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button className="ml-2 flex items-center gap-2 rounded-full border border-border bg-card pl-1 pr-3 py-1 hover:bg-muted/40 transition-colors">
              <div className="flex h-7 w-7 items-center justify-center rounded-full bg-primary/15 text-xs font-semibold text-primary">
                {initials}
              </div>
              <div className="hidden text-left leading-tight sm:block">
                <div className="text-xs font-medium">{userEmail}</div>
                <div className="text-[10px] text-muted-foreground capitalize">{userRole}</div>
              </div>
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-48">
            <DropdownMenuItem onClick={signOut} className="text-destructive focus:text-destructive">
              <LogOut className="mr-2 h-4 w-4" />
              Sign out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
}
